import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Smartphone, Download, Star, ExternalLink, X, Sparkles, CheckCircle2, FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.utiloom.calcnote";
export const UTILOOM_URL = "https://utiloom.in";

// SVG Google Play Icon
export function GooglePlayIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M3.609 1.814L13.792 12 3.61 22.186c-.367-.323-.61-.798-.61-1.341V3.155c0-.543.243-1.018.609-1.341z" fill="#00D2FF" />
      <path d="M17.18 8.613L4.793.882c-.37-.23-.814-.23-1.184 0l10.183 10.183 3.388-2.452z" fill="#00E676" />
      <path d="M3.609 23.118c.37.23.814.23 1.184 0l12.387-7.731-3.388-2.452-10.183 10.183z" fill="#FF3D00" />
      <path d="M20.932 11.233l-3.752-2.345-3.388 2.452 3.388 2.452 3.752-2.345c.61-.38.61-1.834 0-2.214z" fill="#FFC400" />
    </svg>
  );
}

interface CalcNotePromoProps {
  variant?: 'banner' | 'sidebar' | 'mobile' | 'floating';
  collapsed?: boolean;
}

export function CalcNotePromo({ variant = 'banner', collapsed = false }: CalcNotePromoProps) {
  const [dismissed, setDismissed] = useState(false);

  // Variant 1: Sidebar Card
  if (variant === 'sidebar') {
    if (collapsed) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 text-primary hover:bg-primary/20 transition-all border border-primary/20 mx-auto group shadow-sm"
                aria-label="Download CalcNote on Google Play"
              >
                <Smartphone className="h-4 w-4 group-hover:scale-110 transition-transform text-primary" />
              </a>
            </TooltipTrigger>
            <TooltipContent side="right" className="flex flex-col gap-1 max-w-[200px]">
              <span className="font-semibold text-xs text-foreground">CalcNote Mobile App</span>
              <span className="text-[11px] text-muted-foreground">Download free on Google Play by Utiloom</span>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return (
      <div className="relative overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-b from-primary/10 via-primary/5 to-background p-3.5 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-md shadow-primary/25">
            <Smartphone className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <h4 className="font-bold text-sm text-foreground tracking-tight">CalcNote App</h4>
              <span className="inline-flex items-center rounded-md bg-primary/20 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                Free
              </span>
            </div>
            <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5 leading-snug">
              Smart notes + calculator for Android by <a href={UTILOOM_URL} target="_blank" rel="noopener noreferrer" className="font-medium text-foreground hover:text-primary transition-colors underline decoration-dotted">Utiloom</a>
            </p>
          </div>
        </div>

        <div className="mt-3">
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-foreground text-background hover:bg-foreground/90 py-2 px-3 text-xs font-semibold shadow transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
          >
            <GooglePlayIcon className="h-3.5 w-3.5" />
            <span>Get on Play Store</span>
          </a>
        </div>
      </div>
    );
  }

  // Variant 2: Mobile Drawer Card
  if (variant === 'mobile') {
    return (
      <div className="relative overflow-hidden rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/15 via-primary/5 to-card p-4 shadow-sm">
        <div className="flex items-center gap-3 mb-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-tr from-primary to-primary/80 text-primary-foreground shadow-md shadow-primary/30">
            <Smartphone className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-bold text-base text-foreground tracking-tight">CalcNote for Android</h4>
              <span className="flex items-center gap-0.5 text-[11px] font-semibold text-amber-500 bg-amber-500/10 px-1.5 py-0.5 rounded-full">
                <Star className="h-3 w-3 fill-amber-500" />
                4.8
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              by <a href={UTILOOM_URL} target="_blank" rel="noopener noreferrer" className="text-primary font-medium hover:underline">Utiloom</a> • Smart Calculator & Notepad
            </p>
          </div>
        </div>

        <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
          Take notes, write math formulas directly in your text, and manage financial calculations on the go.
        </p>

        <a
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2.5 w-full py-2.5 px-4 rounded-xl bg-foreground text-background font-semibold text-sm shadow-md transition-all active:scale-95"
        >
          <GooglePlayIcon className="h-4 w-4" />
          <span>Download Free on Google Play</span>
        </a>
      </div>
    );
  }

  // Variant 3: Floating Mobile / Bottom CTA Bar
  if (variant === 'floating') {
    if (dismissed) return null;

    return (
      <AnimatePresence>
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 50, opacity: 0 }}
          className="fixed bottom-4 left-4 right-4 z-40 sm:hidden"
        >
          <div className="relative flex items-center justify-between gap-3 rounded-2xl border border-primary/30 bg-background/95 backdrop-blur-md p-3 shadow-xl ring-1 ring-black/5">
            <div className="flex items-center gap-2.5 min-w-0 flex-1">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm">
                <Smartphone className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-foreground truncate">CalcNote on Android</p>
                <p className="text-[11px] text-muted-foreground truncate">Smart utility app by Utiloom</p>
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 rounded-xl bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground shadow transition-transform active:scale-95"
              >
                <GooglePlayIcon className="h-3 w-3" />
                <span>Get App</span>
              </a>
              <button
                onClick={() => setDismissed(true)}
                className="p-1 text-muted-foreground hover:text-foreground rounded-lg"
                aria-label="Dismiss banner"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Variant 4: Standard Full Width Responsive Promo Banner
  return (
    <section className="my-10 overflow-hidden rounded-3xl border border-border/80 bg-gradient-to-br from-card via-card to-primary/5 p-6 sm:p-8 lg:p-10 shadow-sm relative">
      {/* Decorative Glow Elements */}
      <div className="absolute top-0 right-0 -mt-8 -mr-8 h-48 w-48 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 -mb-8 -ml-8 h-48 w-48 rounded-full bg-accent/10 blur-3xl pointer-events-none" />

      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-center">
        {/* Left Info Section */}
        <div className="lg:col-span-8 space-y-4">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
            <Sparkles className="h-3.5 w-3.5" />
            <span>Official Mobile App by Utiloom</span>
          </div>

          <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">
            Get <span className="text-primary">CalcNote</span> for Android
          </h3>

          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed max-w-2xl">
            Experience the power of universal calculations combined with intuitive note-taking. Calculate loan schedules, track daily expenses, and run formulas right inside your documents.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
            <div className="flex items-center gap-2 text-xs sm:text-sm text-foreground/90 font-medium">
              <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
              <span>Smart In-line Math</span>
            </div>
            <div className="flex items-center gap-2 text-xs sm:text-sm text-foreground/90 font-medium">
              <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
              <span>100% Offline & Private</span>
            </div>
            <div className="flex items-center gap-2 text-xs sm:text-sm text-foreground/90 font-medium">
              <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
              <span>Free on Play Store</span>
            </div>
          </div>
        </div>

        {/* Right CTA / Badge Section */}
        <div className="lg:col-span-4 flex flex-col items-center lg:items-end justify-center space-y-3">
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative flex items-center gap-3.5 rounded-2xl bg-foreground text-background px-6 py-3.5 font-semibold text-base shadow-xl transition-all duration-300 hover:scale-[1.03] hover:shadow-2xl active:scale-[0.98] w-full sm:w-auto justify-center"
          >
            <GooglePlayIcon className="h-7 w-7 shrink-0 transition-transform group-hover:scale-110" />
            <div className="text-left leading-tight">
              <div className="text-[10px] uppercase font-medium tracking-wider opacity-80">Download on</div>
              <div className="text-base font-bold tracking-tight">Google Play</div>
            </div>
          </a>

          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>Published by</span>
            <a
              href={UTILOOM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-foreground hover:text-primary transition-colors inline-flex items-center gap-1 underline underline-offset-2"
            >
              Utiloom
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
